/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import swal from 'sweetalert2';
import Loading from '../../components/common/Loading';
import CompanyDetailModal from '../../components/company/CompanyDetailModal';
import CompanyMergeModal from '../../components/company/CompanyMergeModal';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  height: 94vh;
  overflow: scroll;
  width: 100%;
  margin: 16px 16px 16px 0;
  padding: 28px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.04), 0 4px 20px rgba(0, 0, 0, 0.06);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    height: 100vh;
    margin: 0;
    border-radius: 0;
    padding: 16px;
  }
`;

const Toolbar = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 12px;
  flex-wrap: wrap;
`;

const ToolGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SearchInput = styled.input`
  padding: 7px 14px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  width: 240px;
  outline: none;
  transition: border-color 0.15s;
  &:focus { border-color: #007aff; }
`;

const Btn = styled.button`
  padding: 7px 14px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  outline: none;
  white-space: nowrap;
  background: ${({ $variant }) =>
    $variant === 'primary' ? '#007aff' :
    $variant === 'danger' ? '#ef4444' :
    $variant === 'success' ? '#1d6f42' : '#f3f4f6'};
  color: ${({ $variant }) =>
    $variant === 'primary' || $variant === 'danger' || $variant === 'success' ? '#fff' : '#374151'};
  &:hover {
    opacity: 0.85;
  }
`;

const TotalText = styled.span`
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
`;

const TableWrap = styled.table`
  width: 100%;
  border: none;
  border-collapse: collapse;
  td, th {
    text-align: center;
    border: none;
    padding: 10px 12px;
    font-size: 14px;
  }
  tbody tr {
    height: 48px;
    transition: background 0.1s;
    cursor: pointer;
  }
  tbody tr:hover {
    background: rgba(0, 122, 255, 0.04);
  }
  thead > tr {
    background-color: #2d3748;
    height: 44px;
    border-bottom: none;
  }
  thead th {
    font-size: 13px;
    font-weight: 600;
    color: #fff;
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $type }) => $type === 'test' ? '#fef3c7' : '#dbeafe'};
  color: ${({ $type }) => $type === 'test' ? '#92400e' : '#1e40af'};
`;

const Empty = styled.div`
  color: #86868b;
  font-size: 15px;
  padding: 80px 0;
  text-align: center;
  width: 100%;
`;

/* ─── mobile helpers ─── */
const DesktopOnly = styled.div`
  width: 100%;
  @media (max-width: 768px) { display: none; }
`;

const MobileOnly = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    flex: 1;
    overflow-y: auto;
    width: 100%;
  }
`;

const MobileMenuBtn = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: #1c1c1e;
    border-radius: 10px;
    color: #f5f5f7;
    cursor: pointer;
    flex-shrink: 0;
  }
`;

const MSearchIcon = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: #007aff;
    border-radius: 10px;
    cursor: pointer;
    flex-shrink: 0;
  }
`;

const MSearchOverlay = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    position: fixed;
    top: 0; left: 0;
    width: 100%; height: 100%;
    background: rgba(0, 0, 0, 0.4);
    z-index: 2000;
    justify-content: center;
    align-items: flex-start;
    padding-top: 80px;
  }
`;

const MSearchBox = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  width: 90vw;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  animation: ${fadeIn} 0.15s ease;
`;

const MCard = styled.div`
  padding: 14px 0;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  animation: ${fadeIn} 0.2s ease;
  &:active { background: rgba(0, 122, 255, 0.04); }
`;

const MCardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const MCardName = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #111827;
`;

const MCardSub = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const CompanyListPage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [mergeMode, setMergeMode] = useState(false);
  const [mSearchOpen, setMSearchOpen] = useState(false);
  const [mSearchInput, setMSearchInput] = useState('');

  const fetchCompanies = async (query) => {
    try {
      setLoading(true);
      const q = query !== undefined ? query : searchQuery;
      const params = q ? `?search=${encodeURIComponent(q)}` : '';
      const { data } = await axios.get(`/admin/company/list${params}`);
      setCompanies(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies('');
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    fetchCompanies(searchInput);
  };

  const handleSearchReset = () => {
    setSearchInput('');
    setSearchQuery('');
    fetchCompanies('');
  };

  const handleCreate = async () => {
    const { value: formValues } = await swal.fire({
      title: '새 회사 등록',
      html:
        '<input id="swal-name" class="swal2-input" placeholder="대표 회사명 (필수)">' +
        '<input id="swal-official" class="swal2-input" placeholder="공식 법인명 (선택)">',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: '등록',
      cancelButtonText: '취소',
      confirmButtonColor: '#007aff',
      cancelButtonColor: '#a3a3a3',
      preConfirm: () => {
        const name = document.getElementById('swal-name').value.trim();
        if (!name) {
          swal.showValidationMessage('회사명을 입력해주세요.');
          return false;
        }
        return {
          name,
          official_name: document.getElementById('swal-official').value.trim(),
        };
      },
    });
    if (formValues) {
      try {
        await axios.post('/admin/company/create', {
          name: formValues.name,
          official_name: formValues.official_name,
          aliases: [formValues.name],
        });
        swal.fire({ text: '등록 완료', icon: 'success', showConfirmButton: false, timer: 1000 });
        fetchCompanies();
      } catch (err) {
        swal.fire({ text: err.response?.data?.message || '오류 발생', icon: 'error', showConfirmButton: false, timer: 1500 });
      }
    }
  };

  const handleDelete = async (e, companyId, companyName) => {
    e.stopPropagation();
    const result = await swal.fire({
      text: `${companyName}을(를) 삭제하시겠습니까? 소속 회원은 미배정 상태가 됩니다.`,
      icon: 'warning',
      iconColor: '#d33',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#a3a3a3',
      confirmButtonText: '삭제',
      cancelButtonText: '취소',
    });
    if (result.isConfirmed) {
      try {
        await axios.patch(`/admin/company/delete/${companyId}`);
        swal.fire({ text: '삭제 완료', icon: 'success', showConfirmButton: false, timer: 1000 });
        fetchCompanies();
      } catch {
        swal.fire({ text: '오류 발생', icon: 'error', showConfirmButton: false, timer: 1000 });
      }
    }
  };

  return (
    <Container>
      <div style={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>

        {/* header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <MobileMenuBtn onClick={() => window.dispatchEvent(new Event('open-sidebar'))}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </MobileMenuBtn>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>회사 관리</h2>
          </div>
          <MSearchIcon onClick={() => { setMSearchInput(searchInput); setMSearchOpen(true); }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </MSearchIcon>
        </div>

        {/* mobile info row */}
        <MobileOnly style={{ overflow: 'visible', flex: 'none', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <TotalText>{companies.length}개</TotalText>
              {searchQuery && (
                <>
                  <TotalText>&middot; &quot;{searchQuery}&quot;</TotalText>
                  <Btn onClick={handleSearchReset} style={{ padding: '3px 8px', fontSize: 11 }}>초기화</Btn>
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <Btn $variant="success" onClick={handleCreate} style={{ padding: '5px 10px', fontSize: 12 }}>+ 등록</Btn>
              <Btn onClick={() => setMergeMode(true)} style={{ padding: '5px 10px', fontSize: 12 }}>병합</Btn>
            </div>
          </div>
        </MobileOnly>

        {/* mobile search dialog */}
        {mSearchOpen && (
          <MSearchOverlay onClick={() => setMSearchOpen(false)}>
            <MSearchBox onClick={(e) => e.stopPropagation()}>
              <form onSubmit={(e) => {
                e.preventDefault();
                setSearchInput(mSearchInput);
                setSearchQuery(mSearchInput);
                fetchCompanies(mSearchInput);
                setMSearchOpen(false);
              }}>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>검색</div>
                <SearchInput
                  autoFocus
                  placeholder="회사명, 법인명, 별칭"
                  value={mSearchInput}
                  onChange={(e) => setMSearchInput(e.target.value)}
                  style={{ width: '100%', marginBottom: 12 }}
                />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Btn type="button" onClick={() => setMSearchOpen(false)}>취소</Btn>
                  <Btn $variant="primary" type="submit">검색</Btn>
                </div>
              </form>
            </MSearchBox>
          </MSearchOverlay>
        )}

        {/* desktop toolbar */}
        <DesktopOnly>
          <Toolbar>
            <ToolGroup>
              <form onSubmit={handleSearch} style={{ display: 'flex', gap: 6 }}>
                <SearchInput
                  placeholder="회사명, 법인명, 별칭 검색"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                <Btn $variant="primary" type="submit">검색</Btn>
              </form>
              {searchQuery && <Btn onClick={handleSearchReset}>초기화</Btn>}
            </ToolGroup>
            <ToolGroup>
              <Btn $variant="success" onClick={handleCreate}>+ 회사 등록</Btn>
              <Btn onClick={() => setMergeMode(true)}>회사 병합</Btn>
              <TotalText>총 {companies.length}개</TotalText>
            </ToolGroup>
          </Toolbar>
        </DesktopOnly>

        {loading ? (
          <Loading />
        ) : companies.length === 0 ? (
          <Empty>{searchQuery ? '검색 결과가 없습니다.' : '등록된 회사가 없습니다.'}</Empty>
        ) : (
          <>
            {/* desktop table */}
            <DesktopOnly>
              <TableWrap>
                <thead>
                  <tr>
                    <th>대표명</th>
                    <th>법인명</th>
                    <th>별칭</th>
                    <th>소속 회원수</th>
                    <th>구분</th>
                    <th>삭제</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((c) => (
                    <tr key={c._id} onClick={() => setSelectedId(c._id)}>
                      <td style={{ fontWeight: 600 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {c.name}
                          {c.admin_memo && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" title="메모 있음">
                              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                            </svg>
                          )}
                        </span>
                      </td>
                      <td style={{ color: '#6b7280' }}>{c.official_name || '-'}</td>
                      <td style={{ color: '#9ca3af', fontSize: 12, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.aliases.join(', ')}
                      </td>
                      <td>{c.user_count}명</td>
                      <td>
                        {c.is_test ? <Badge $type="test">테스트</Badge> : <Badge>일반</Badge>}
                      </td>
                      <td>
                        <Btn $variant="danger" style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={(e) => handleDelete(e, c._id, c.name)}>
                          삭제
                        </Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
            </DesktopOnly>

            {/* mobile card list */}
            <MobileOnly>
              {companies.map((c) => (
                <MCard key={c._id} onClick={() => setSelectedId(c._id)}>
                  <MCardTop>
                    <MCardName>
                      {c.name}
                      {c.admin_memo && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 4, verticalAlign: 'middle' }}>
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                      )}
                    </MCardName>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {c.is_test ? <Badge $type="test">테스트</Badge> : null}
                      <span style={{ fontSize: 13, color: '#6b7280' }}>{c.user_count}명</span>
                    </div>
                  </MCardTop>
                  {c.official_name && <MCardSub>{c.official_name}</MCardSub>}
                  <MCardSub style={{ fontSize: 12, color: '#9ca3af' }}>{c.aliases.join(', ')}</MCardSub>
                </MCard>
              ))}
            </MobileOnly>
          </>
        )}
      </div>

      {selectedId && (
        <CompanyDetailModal
          companyId={selectedId}
          onClose={() => { setSelectedId(null); fetchCompanies(); }}
        />
      )}
      {mergeMode && (
        <CompanyMergeModal
          companies={companies}
          onClose={() => { setMergeMode(false); fetchCompanies(); }}
        />
      )}
    </Container>
  );
};

export default CompanyListPage;
